import React, { useEffect, useState } from "react";
import { FateFormula } from "../../../types/types";
import { FateFormulaService } from "../../../services/fateFormulaService";
import { Modal } from "../../ui";

export interface FateFormulaPickerProps {
  campaignId?: number;
  onPick: (_f: FateFormula) => void;
  onClose: () => void;
}

const FateFormulaPicker: React.FC<FateFormulaPickerProps> = ({ campaignId, onPick, onClose }) => {
  const [userFormulas, setUserFormulas] = useState<FateFormula[]>([]);
  const [campaignFormulas, setCampaignFormulas] = useState<FateFormula[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    FateFormulaService.listUser()
      .then(setUserFormulas)
      .catch(() => setUserFormulas([]));
    if (campaignId) {
      FateFormulaService.listCampaign(campaignId)
        .then(setCampaignFormulas)
        .catch(() => setCampaignFormulas([]));
    }
  }, [campaignId]);

  const q = search.toLowerCase();
  const filter = (f: FateFormula) => !q || f.name.toLowerCase().includes(q);

  const filteredUser = userFormulas.filter(filter);
  const filteredCampaign = campaignFormulas.filter(filter);

  return (
    <Modal isOpen onClose={onClose} title="Pick a Fate Formula" size="md">
      <div className="px-6 py-4 border-b border-zinc-800">
        <h2 className="text-sm font-semibold text-amber-400 tracking-wide uppercase mb-3">
          Add from Library
        </h2>
        <input
          type="text"
          placeholder="Search formulas…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400"
        />
      </div>

      <div className="overflow-y-auto max-h-[60vh]">
        <FormulaGroup label="My Formulas" formulas={filteredUser} onPick={onPick} />
        {campaignId !== undefined && (
          <FormulaGroup label="Campaign Formulas" formulas={filteredCampaign} onPick={onPick} />
        )}
      </div>

      <div className="px-6 py-3 border-t border-zinc-800 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 transition-colors"
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default FateFormulaPicker;

// ── Sub-component ─────────────────────────────────────────────────────────────

interface FormulaGroupProps {
  label: string;
  formulas: FateFormula[];
  onPick: (_f: FateFormula) => void;
}

const FormulaGroup: React.FC<FormulaGroupProps> = ({ label, formulas, onPick }) => (
  <div>
    <p className="px-6 py-2 text-[10px] font-semibold text-zinc-500 uppercase tracking-widest bg-zinc-800/40 border-b border-zinc-800">
      {label}
    </p>
    {formulas.length === 0 ? (
      <p className="px-6 py-3 text-xs text-zinc-600 italic">No formulas</p>
    ) : (
      <ul className="divide-y divide-zinc-800">
        {formulas.map((f) => (
          <li key={f.id}>
            <button
              onClick={() => onPick(f)}
              className="w-full text-left px-6 py-3 hover:bg-zinc-800/50 transition-colors"
            >
              <span className="text-sm font-medium text-white">{f.name}</span>
              <span className="ml-2 text-xs text-zinc-500">
                {f.diceNotation}
                {f.modifier !== 0 && (f.modifier > 0 ? `+${f.modifier}` : f.modifier)}
                {f.skillType && ` · ${f.skillType}`}
              </span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);
